import React from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';

import SignInFormComponent from './signInForm';
import SignInBlockedComponent from './signInBlocked';

import {userSignInAction, userSignInError, clearUserSignInError} from 'store/actions/authActions';
import UserService from 'services/UserService';

type SignInComponentProps = {
  showLoginFailedMessage: boolean;
  showServerErrorMessage: boolean;
  loginFailedMessage: string;
  twoFactorRequired: boolean;
  twoFactorMethods: string;
};

type SignInComponentState = {
  identifier: string;
  password: string;
  qrpin: string;
  smscode: string;
  currentStep: string;
  isSendingSms: boolean;
};

class SignInComponent extends React.Component<SignInComponentProps, SignInComponentState> {

  constructor(props) {
    super(props);
    this.state = {
      identifier: '',
      password: '',
      qrpin: '',
      smscode: '',
      currentStep: 'qr',
      isSendingSms: false
    };
  }

  hasQr = () => (this.props.twoFactorMethods || '').includes('qr');
  hasSms = () => (this.props.twoFactorMethods || '').includes('sms');

  componentDidUpdate(prevProps) {
    // Credentials accepted, 2FA now required: show the first field. For an SMS-only user we send
    // the code straight away; for a dual-method user the SMS is sent after the authenticator step.
    if (this.props.twoFactorRequired && !prevProps.twoFactorRequired) {
      const step = this.hasQr() ? 'qr' : 'sms';
      this.setState({currentStep: step, qrpin: '', smscode: ''});
      if (step === 'sms') {
        this.sendSms();
      }
    }
  }

  getReturnUrl = () =>
    this.props.location.state === undefined ? null : this.props.location.state.from.pathname;

  onLogin = () => {
    this.props.userSignInAction(this.state.identifier, this.state.password, this.getReturnUrl());
  };

  onValidateTwoFactor = () => {
    this.props.clearUserSignInError();

    if (this.state.currentStep === 'qr' && this.hasSms()) {
      // Both methods: validate the authenticator PIN and send the SMS, then switch to the SMS field.
      this.sendSms(this.state.qrpin);
      return;
    }

    if (this.state.currentStep === 'qr') {
      // Authenticator only.
      this.props.userSignInAction(this.state.identifier, this.state.password, this.getReturnUrl(), this.state.qrpin);
      return;
    }

    // SMS step.
    this.props.userSignInAction(this.state.identifier, this.state.password, this.getReturnUrl(), undefined, this.state.smscode);
  };

  sendSms = async (qrpin) => {
    this.setState({isSendingSms: true});
    this.props.clearUserSignInError();
    try {
      await UserService.sendLoginSmsCode(this.state.identifier, this.state.password, qrpin);
      this.setState({currentStep: 'sms', smscode: '', isSendingSms: false});
    } catch (e) {
      // Wrong authenticator PIN (dual-method) or an SMS send failure: surface the 2FA error.
      this.setState({isSendingSms: false});
      this.props.userSignInError({error: 'invalid_2fa', errorDescription: ''});
    }
  };

  handleLoginChange = (event) => this.setState({identifier: event.target.value});
  handlePasswordChange = (event) => this.setState({password: event.target.value});

  handleQrpinChange = (event) => {
    this.setState({qrpin: event.target.value});
    if (this.props.loginFailedMessage) this.props.clearUserSignInError();
  };

  handleSmscodeChange = (event) => {
    this.setState({smscode: event.target.value});
    if (this.props.loginFailedMessage) this.props.clearUserSignInError();
  };

  render() {
    if (this.props.loginFailedMessage === 'blocked_user') {
      return (<SignInBlockedComponent />);
    }
    return (
      <SignInFormComponent
        identifier={this.state.identifier}
        password={this.state.password}
        qrpin={this.state.qrpin}
        smscode={this.state.smscode}
        twoFactorRequired={this.props.twoFactorRequired}
        currentStep={this.state.currentStep}
        isSendingSms={this.state.isSendingSms}
        handleLoginChange={this.handleLoginChange}
        handlePasswordChange={this.handlePasswordChange}
        handleQrpinChange={this.handleQrpinChange}
        handleSmscodeChange={this.handleSmscodeChange}
        onLogin={this.onLogin}
        onValidateTwoFactor={this.onValidateTwoFactor}
      />
    );
  }
}

const mapStateToProps = state => ({
  showLoginFailedMessage: state.auth.showLoginFailedMessage,
  loginFailedMessage: state.auth.loginFailedMessage,
  showServerErrorMessage: state.auth.showServerErrorMessage,
  twoFactorRequired: state.auth.twoFactorRequired,
  twoFactorMethods: state.auth.twoFactorMethods
});

export default withRouter(connect(mapStateToProps, {userSignInAction, userSignInError, clearUserSignInError})(SignInComponent));
